/**
 * Registry of admin-configurable practice controls.
 *
 * This is the single source of truth that drives the Practice Admin view
 * (see src/Pages/PracticeAdmin.jsx). It is intentionally data-driven so new
 * controls — and entirely new groups/control types — can be added here without
 * touching the rendering code.
 *
 * Storage model (Firestore): each setting is persisted at
 *   practices/{slug}.settings[group.storageKey][control.key]
 * so, for example, the "practiceCaseConfirmation" email toggle lives at
 *   practices/{slug}.settings.emails.practiceCaseConfirmation
 *
 * A missing value means the control is at its `defaultValue`. Email and
 * return-logistics toggles default on, so existing practices keep their current
 * behavior until an admin turns something off. Printing a body bag tag from a
 * draft defaults off and the toggle is disabled; turn it on for a practice by
 * setting `settings.bodyBagTag.printFromDraft` to true. The backend mirror of
 * the email keys lives in functions/config/practice-settings.js — keep the keys
 * in sync across the two files.
 *
 * The return logistics group is derived from the option catalog in
 * src/config/returnLogisticsOptions.js, which the intake form renders from — add
 * or remove a return option there and its toggle appears here automatically.
 */

import {
  RETURN_LOGISTICS_DEFAULT_VALUE,
  RETURN_LOGISTICS_OPTIONS,
  RETURN_LOGISTICS_STORAGE_KEY,
} from "./returnLogisticsOptions";

export const CONTROL_TYPES = {
  TOGGLE: "toggle",
};

// Firestore sub-map the intake form field toggles live under:
// settings.formFields.*
export const FORM_FIELDS_STORAGE_KEY = "formFields";

export const FORM_FIELD_KEYS = {
  SUBMITTER_INFO: "submitterInfo",
};

export const CONTROL_GROUPS = [
  {
    id: "emails",
    // Firestore sub-map these controls are stored under (settings.emails.*)
    storageKey: "emails",
    title: "Case Notification Emails",
    description:
      "Choose which automated emails go out when a new case is submitted from your practice.",
    controls: [
      {
        key: "practiceCaseConfirmation",
        type: CONTROL_TYPES.TOGGLE,
        label: "Practice case confirmation",
        description:
          "Sent to your practice inbox with the intake PDF attached.",
        defaultValue: true,
      },
      {
        key: "petFamilyCaseConfirmation",
        type: CONTROL_TYPES.TOGGLE,
        label: "Pet family case confirmation email",
        description: "The follow-up email sent to the pet family.",
        defaultValue: true,
        disabled: true,
      },
    ],
  },
  {
    id: "formFields",
    storageKey: FORM_FIELDS_STORAGE_KEY,
    title: "Intake Form Fields",
    description: "Choose which information your case form will collect.",
    controls: [
      {
        key: FORM_FIELD_KEYS.SUBMITTER_INFO,
        type: CONTROL_TYPES.TOGGLE,
        label: "Submitter info",
        description:
          "The name of the person submitting the case. Turning this off will hide the field from the case form and default to the signed-in user's name.",
        defaultValue: true,
      },
    ],
  },
  {
    id: "returnLogistics",
    storageKey: RETURN_LOGISTICS_STORAGE_KEY,
    title: "Return Logistics Options",
    description:
      "Choose which return options your team can pick from when submitting a case. Turning all of them off hides the question entirely.",
    controls: RETURN_LOGISTICS_OPTIONS.map((option) => ({
      key: option.key,
      type: CONTROL_TYPES.TOGGLE,
      label: option.adminLabel,
      description: option.adminDescription,
      defaultValue: RETURN_LOGISTICS_DEFAULT_VALUE,
      disabled: option.disabled,
    })),
  },
  {
    id: "bodyBagTag",
    storageKey: "bodyBagTag",
    title: "Body Bag Tag",
    description:
      "Printing a tag from the intake form is off. Submitted cases can still download their tag.",
    controls: [
      {
        key: "printFromDraft",
        type: CONTROL_TYPES.TOGGLE,
        label: "Print from drafts",
        description:
          "Show a button on the intake form so staff can print a body bag tag before the case is submitted.",
        defaultValue: false,
        disabled: true,
      },
    ],
  },
];

/**
 * Resolves the effective value of a control from a stored settings object,
 * falling back to the control's declared default when unset.
 * @param {Object|undefined} settings - The practice's `settings` map.
 * @param {Object} group - A group from CONTROL_GROUPS.
 * @param {Object} control - A control within that group.
 * @returns {boolean} The effective value.
 */
export function getControlValue(settings, group, control) {
  const stored = settings?.[group.storageKey]?.[control.key];
  return stored === undefined ? control.defaultValue : stored;
}

/**
 * Whether an intake form field is enabled for a practice. Same opt-out model as
 * the other controls: a missing value means the field is shown, so practices
 * that never open their settings get the field by default.
 *
 * @param {Object|undefined} settings - The practice's `settings` map.
 * @param {string} key - One of FORM_FIELD_KEYS.
 * @returns {boolean}
 */
export function isFormFieldEnabled(settings, key) {
  return settings?.[FORM_FIELDS_STORAGE_KEY]?.[key] !== false;
}

/**
 * Whether staff can print a body bag tag from the intake form before submit.
 * Missing settings stay off.
 * @param {Object|undefined} settings - The practice's `settings` map.
 * @returns {boolean}
 */
export function isPrintFromDraftEnabled(settings) {
  const group = CONTROL_GROUPS.find((entry) => entry.id === "bodyBagTag");
  const control = group?.controls.find(
    (entry) => entry.key === "printFromDraft"
  );
  if (!group || !control) return false;
  return getControlValue(settings, group, control);
}
